<template>
    <div>
        <headline-container headline="Import People" :breadcrumbs="breadcrumbs"></headline-container>

        <div class="container">
            <div class="row">
                <div class="col-xs-6">
                    <h3>
                        Import People
                    </h3>

                    <p>
                        On this page, you may import people from an Excel or CSV file.
                    </p>

                    <p>
                        First off, upload the file containing the person data. The system will analyze the file and
                        figure out which columns that are present.
                    </p>

                    <p>
                        When the file has been analyzed, you may map the columns in the file to the fields that exist on
                        a person in the system.
                    </p>

                    <p>
                        After mapping the columns, save the import. The system will then process the people. Notice
                        that the processing may take several seconds to complete.
                    </p>
                </div>

                <div class="col-xs-18">
                    <form @submit.prevent="submit">
                        <div class="panel panel-default form-horizontal">
                            <div class="panel-heading">
                                <h5>
                                    Step 1: Upload File
                                </h5>
                            </div>

                            <div class="panel-body">
                                <form-group name="file" label="File" :error="getError('file')">
                                    <input
                                            type="file"
                                            class="form-control"
                                            id="file"
                                            @change="file = $event.target.files[0]"
                                    >
                                </form-group>

                                <hr>

                                <div class="form-group">
                                    <div class="col-sm-offset-8 col-sm-12">
                                        <button type="button" class="btn btn-black" @click="getHeadings">
                                            <i class="fa fa-cloud-upload-alt"></i> Get Headings
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="headingsLoaded">
                            <div class="panel panel-default form-horizontal">
                                <div class="panel-heading">
                                    <h5>
                                        Step 2: Map Columns
                                    </h5>
                                </div>

                                <div class="panel-body">
                                    <select-form-group
                                            v-model="columns.name"
                                            name="name"
                                            label="Name"
                                            :error="getError('name')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>

                                    <select-form-group
                                            v-model="columns.company_cvr"
                                            name="company_cvr"
                                            label="Company CVR"
                                            :error="getError('company_cvr')"
                                            :options="headingOptions"
                                            :nullable="true"
                                            help-text="Link the person to the company with the given CVR"
                                    ></select-form-group>

                                    <select-form-group
                                            v-model="columns.email"
                                            name="email"
                                            label="Email"
                                            :error="getError('email')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>

                                    <select-form-group
                                            v-model="columns.phone"
                                            name="phone"
                                            label="Phone Number"
                                            :error="getError('phone')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>
                                    
                                    <hr>

                                    <select-form-group
                                            v-model="columns.address_street_name"
                                            name="address_street_name"
                                            label="Address Street Name"
                                            :error="getError('address_street_name')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>

                                    <select-form-group
                                            v-model="columns.address_street_number"
                                            name="address_street_number"
                                            label="Address Street Number"
                                            :error="getError('address_street_number')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>

                                    <select-form-group
                                            v-model="columns.address_street_letter"
                                            name="address_street_letter"
                                            label="Address Street Letter"
                                            :error="getError('address_street_letter')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>

                                    <select-form-group
                                            v-model="columns.address_street_floor"
                                            name="address_street_floor"
                                            label="Address Street Floor"
                                            :error="getError('address_street_floor')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>

                                    <select-form-group
                                            v-model="columns.address_street_location"
                                            name="address_street_location"
                                            label="Address Street Location"
                                            :error="getError('address_street_location')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>

                                    <select-form-group
                                            v-model="columns.address_zip_code"
                                            name="address_zip_code"
                                            label="Address Zip Code"
                                            :error="getError('address_zip_code')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>

                                    <select-form-group
                                            v-model="columns.address_city"
                                            name="address_city"
                                            label="Address City"
                                            :error="getError('address_city')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>

                                    <select-form-group
                                            v-model="columns.address_country"
                                            name="address_country"
                                            label="Address Country"
                                            :error="getError('address_country')"
                                            :options="headingOptions"
                                            :nullable="true"
                                    ></select-form-group>

                                    <hr>

                                    <div class="form-group">
                                        <div class="col-sm-offset-8 col-sm-12">
                                            <button type="submit" class="btn btn-black" style="margin-bottom: 22px;" :disabled="processing">
                                                <span v-if="processing">
                                                    <i class="fa fa-spinner fa-spin fa-fw"></i> Processing...                                
                                                </span>

                                                <span v-else>
                                                    <i class="fa fa-cloud-upload-alt"></i> Upload
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="rowErrors.length" class="alert alert-danger">
                                <strong>
                                    A row failed to validate!
                                </strong>

                                <ul>
                                    <li v-for="rowError in rowErrors">
                                        {{ rowError[0] }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import http from '@/http'
    import notify from '@/notify'
    import Errors from '@/mixins/Errors'
    import FormGroup from '@/vendor/forms/FormGroups/Horizontal/FormGroup'
    import HeadlineContainer from '@/components/Layout/HeadlineContainer'
    import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'

    export default {
        components: {FormGroup, HeadlineContainer, SelectFormGroup},

        mixins: [Errors],

        data() {
            return {
                file: null,
                headings: [],
                headingsLoaded: false,
                columns: {},
                rowErrors: [],
                processing: false
            }
        },

        computed: {
            breadcrumbs() {
                return [
                    {url: '/people', text: 'People'},
                    {text: 'Import People'}
                ]
            },

            headingOptions() {
                return this.headings.data.map(heading => {
                    return {
                        value: heading.name,
                        label: heading.name
                    }
                })
            }
        },

        methods: {
            submit() {
                this.processing = true

                this.rowErrors = []

                this.errors = {}

                http.post('/api/v1/person-imports', this.getFormData())
                    .then(() => {
                        notify.success('The people were imported')

                        this.$router.push('/people')
                    })
                    .catch(error => {
                        if (error.response.status === 400) {
                            return this.rowErrors = error.response.data.errors
                        }

                        throw error
                    })
                    .catch(this.catchValidationErrors)
                    .then(() => {
                        this.processing = false
                    })
            },

            getFormData() {
                let formData = new FormData()

                formData.append('file', this.file)

                Object.keys(this.columns).forEach(key => formData.append(key, this.columns[key]));

                return formData
            },

            getHeadings() {
                this.columns = {}

                http.post('/api/v1/excel-heading-analyses', this.getHeadingsFormData())
                    .then(response => {
                        this.headings = response.data

                        this.matchColumns()
                        
                        this.headingsLoaded = true
                    })
                    .catch(this.catchValidationErrors)
            },

            getHeadingsFormData() {
                let formData = new FormData()

                formData.append('file', this.file)

                return formData
            },


            matchColumns() {
                let search = [
                    'name',
                    'company_cvr',
                    'email',
                    'phone',
                    'address_street_name',
                    'address_street_number',
                    'address_street_letter',
                    'address_street_floor',
                    'address_street_location',
                    'address_zip_code',
                    'address_city',
                    'address_country'
                ]

                this.headings.data.forEach(heading => {
                    search.forEach(value => {
                        if (heading.name.toLowerCase().replace(/ /g, '_') === value) {
                            this.columns[value] = heading.name

                        }
                    })
                })
            }
        }
    }
</script>
